import * as React from 'react';
import { NavLink, Outlet } from 'react-router';
import { cn } from '../lib/cn';
import { tabPatterns } from '../design-system/patterns/tabs';
import { focusUtils } from '../design-system/utils/a11y';
import type { UniversalTabItem } from './UniversalTabs';

export interface UniversalVerticalTabsProps {
  items: UniversalTabItem[];
  /** When set, renders the matched route inside via `<Outlet />`. Default true. */
  withOutlet?: boolean;
  className?: string;
}

/**
 * UniversalVerticalTabs — settings-style vertical tab list with right-side content.
 *
 * Use for settings pages (e.g. Profile / Security / Billing / Notifications).
 * Each item is a child route; the layout renders the active route via `<Outlet />`.
 */
export function UniversalVerticalTabs({
  items,
  withOutlet = true,
  className,
}: UniversalVerticalTabsProps) {
  return (
    <div className={cn(tabPatterns.vertical.wrapper, className)}>
      <nav
        className={tabPatterns.vertical.list}
        role="tablist"
        aria-label="Settings"
      >
        {items.map((item) => (
          <VerticalTabLink key={item.to} item={item} />
        ))}
      </nav>

      {withOutlet && (
        <div className={tabPatterns.vertical.content}>
          <Outlet />
        </div>
      )}
    </div>
  );
}

function VerticalTabLink({ item }: { item: UniversalTabItem }) {
  const Icon = item.icon;

  if (item.disabled) {
    return (
      <span
        className={cn(tabPatterns.vertical.trigger, 'cursor-not-allowed opacity-50')}
        aria-disabled="true"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        {item.label}
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      role="tab"
      className={({ isActive }) =>
        cn(
          isActive ? tabPatterns.vertical.triggerActive : tabPatterns.vertical.trigger,
          focusUtils.ring
        )
      }
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge !== undefined && (
        <span className="rounded-pill bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}
